import React, { useMemo, memo } from "react";
import { downloadExcel } from "react-export-table-to-excel";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import { map, has, startCase, isNull, flatMap, isEmpty } from "lodash";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { notesApi } from "../../services/notesApi";
import {
  ENTITY_TYPES,
  NOTE_TYPE_VALUE,
  prettyNumericRound,
  getFormattedNumber,
} from "../../utils";
import { getTierLevelForQty } from "../../utils/pogCalculation";
import useStableCallback from "../../hooks/useStableCallback";
import useColumns from "../../hooks/useColumns";
import getPlanogramItemColumns from "../../pages/SubAccordion/pogItemTableColumns";
import getOrderItemColumns from "../../pages/SubAccordion/orderItemTableColumns";

const exportButtonSx = {
  "& .MuiButton-endIcon": {
    margin: 0,
  },
  "& .MuiSvgIcon-root": {
    fontSize: 16,
  },
};

const pick = (object, keys) => {
  let result = {};
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    if (has(object, key)) {
      result = {
        ...result,
        [key]: !isNull(object[key]) ? object[key] : "",
      };
    } else {
      result = {
        ...result,
        [key]: "",
      };
    }
  }
  return result;
};

const getNoteValue = (list, id) => {
  const matchedNote = list.find((note) => note.NotesId === id);
  return matchedNote?.Notes ?? "";
};

const valueMapper =
  ({
    extractedFields,
    selectedIds,
    store,
    entityType,
    retailerNotesData,
    plannerNotesData,
  }) =>
  (row) => {
    let otherRowValues = {};
    const isSelected = selectedIds.includes(row.id);

    otherRowValues = {
      store: !!store?.memberNumber ? store?.memberNumber : "",
      storeName: !!store?.name ? store?.name : "",
      selected: isSelected ? "Y" : "N",
    };
    if (extractedFields.includes("multiplier")) {
      const multiplierVal = row?.multiplier ? `${row?.multiplier}x` : "1x";
      otherRowValues = {
        ...otherRowValues,
        multiplier: multiplierVal,
      };
    }
    if (extractedFields.includes("discMultiplier")) {
      const disMultiplierVal = row?.discMultiplier
        ? `${row?.discMultiplier}x`
        : "1x";
      otherRowValues = {
        ...otherRowValues,
        discMultiplier: disMultiplierVal,
      };
    }
    if ([ENTITY_TYPES.planogram, ENTITY_TYPES.orders].includes(entityType)) {
      if (extractedFields.includes("region")) {
        const regionValue = row?.region ? row?.region : "-";
        otherRowValues = {
          ...otherRowValues,
          region: regionValue,
        };
      }
      if (extractedFields.includes("planogram")) {
        otherRowValues = {
          ...otherRowValues,
          planogram: `${row?.PlanogramNumber}`,
        };
      }
      if (extractedFields.includes("core")) {
        otherRowValues = {
          ...otherRowValues,
          core: row?.core === "Y" ? "Yes" : "No",
        };
      }
      if (extractedFields.includes("pogcost")) {
        const costValue = prettyNumericRound(row?.pogcost || 0);
        otherRowValues = {
          ...otherRowValues,
          pogcost: `$${getFormattedNumber(costValue)}`,
        };
      }
      if (extractedFields.includes("adjcost")) {
        const costValue = prettyNumericRound(row?.adjcost || 0);
        otherRowValues = {
          ...otherRowValues,
          adjcost: `$${getFormattedNumber(costValue)}`,
        };
      }
      if (extractedFields.includes("totalCost")) {
        const costValue = prettyNumericRound(row?.totalCost || 0);
        otherRowValues = {
          ...otherRowValues,
          totalCost: `$${getFormattedNumber(costValue)}`,
        };
      }
      if (extractedFields.includes("tier")) {
        const tierLevel = getTierLevelForQty(row);
        otherRowValues = {
          ...otherRowValues,
          tier: tierLevel && !!row.Item_Nbr ? `T${tierLevel}` : "-",
        };
      }
      if (extractedFields.includes("discountPercentage")) {
        const discountValue = !isNull(row?.discountPercentage)
          ? row?.discountPercentage
          : 0;
        otherRowValues = {
          ...otherRowValues,
          discountPercentage:
            discountValue === undefined ? "" : `${discountValue}%`,
        };
      }
    }
    if (ENTITY_TYPES.planogram === entityType) {
      if (has(row, "plannernotesid")) {
        otherRowValues = {
          ...otherRowValues,
          plannernotesid: getNoteValue(plannerNotesData, row.plannernotesid),
        };
      }
      if (has(row, "retailernotesid")) {
        otherRowValues = {
          ...otherRowValues,
          retailernotesid: getNoteValue(retailerNotesData, row.retailernotesid),
        };
      }
      if (has(row, "PlannerAction")) {
        otherRowValues = {
          ...otherRowValues,
          PlannerAction: row?.PlannerAction ? "Completed" : "",
        };
      }
      if (has(row, "RetailerAction")) {
        otherRowValues = {
          ...otherRowValues,
          RetailerAction: row?.RetailerAction ? "Completed" : "",
        };
      }
    }
    if (ENTITY_TYPES.orders === entityType) {
      if (extractedFields.includes("orderCost")) {
        const costValue = prettyNumericRound(row?.orderCost || 0);
        otherRowValues = {
          ...otherRowValues,
          orderCost: `$${getFormattedNumber(costValue)}`,
        };
      }
      if (extractedFields.includes("ReleaseDateTime")) {
        const dateTime = row?.ReleaseDateTime
          ? dayjs(row?.ReleaseDateTime).format("MM/DD/YYYY")
          : "";
        otherRowValues = {
          ...otherRowValues,
          ReleaseDateTime: dateTime,
        };
      }
    }
    if (ENTITY_TYPES.discount === entityType) {
      if (extractedFields.includes("planogram")) {
        otherRowValues = {
          ...otherRowValues,
          planogram: `${row?.PlanogramNumber}`,
        };
      }
      if (extractedFields.includes("CoreDiscountPercent")) {
        const discount = row?.CoreDiscountPercent
          ? `${row?.CoreDiscountPercent}%`
          : "";
        otherRowValues = {
          ...otherRowValues,
          CoreDiscountPercent: discount,
        };
      }
      if (extractedFields.includes("NonCoreDiscountPercent")) {
        const discount = row?.NonCoreDiscountPercent
          ? `${row?.NonCoreDiscountPercent}%`
          : "";
        otherRowValues = {
          ...otherRowValues,
          NonCoreDiscountPercent: discount,
        };
      }
    }
    return otherRowValues;
  };

const handleValues = (rows, extractedFields, mapper) => {
  let content = [];
  for (let i = 0; i < rows.length; i++) {
    let row = rows[i];
    content.push({
      ...pick(row, extractedFields),
      ...mapper(row),
    });
  }
  return content;
};

const setTableContent =
  ({
    body,
    entityType,
    isExportAll,
    selectedIds,
    store,
    retailerNotesData,
    plannerNotesData,
  }) =>
  (extractedFields) => {
    let itemContent = [];
    const itemValueMapper = valueMapper({
      extractedFields,
      entityType,
      selectedIds,
      retailerNotesData,
      plannerNotesData,
      store,
    });

    const getItemContent = (rows = []) => {
      itemContent = handleValues(rows, extractedFields, itemValueMapper);
    };

    const pogContent = map(body, (item) => {
      let otherMappedValue = itemValueMapper(item);

      return [
        {
          ...pick(item, extractedFields),
          ...otherMappedValue,
        },
        ...itemContent,
      ];
    });

    const tableContent = pogContent.reduce((acc, pog) => {
      return [...acc, ...pog];
    }, []);

    return tableContent;
  };

const extractSelections = (selections) => {
  let combinedSelections = [];
  const scopes = Object.keys(selections) || [];
  for (let i = 0; i < scopes.length; i++) {
    const scope = scopes[i];
    const pogList = selections[scope];
    const ids = flatMap(pogList);
    combinedSelections = [...combinedSelections, ...ids];
  }
  return combinedSelections;
};

const ExportButton = ({
  body = [],
  isExportAll = false,
  selections = {},
  displayItem = false,
}) => {
  const store = useSelector((store) => store.global.store);
  const { entityType } = useSelector((store) => store.global.settings);
  const { headers: discountHeaders } = useSelector(
    (store) => store.global.report
  );

  const { updatedColumns } = useColumns({
    entityType,
    isDisplayItem: displayItem,
  });

  const selectedIds = useMemo(
    () =>
      ENTITY_TYPES.discount === entityType
        ? flatMap(selections)
        : isExportAll
        ? extractSelections(selections)
        : isEmpty(selections)
        ? []
        : selections,
    [entityType, selections, isExportAll]
  );

  const headers = useMemo(() => {
    let columns = [];
    switch (entityType) {
      case ENTITY_TYPES.discount:
        columns = isExportAll ? discountHeaders : updatedColumns;
        break;
      case ENTITY_TYPES.planogram:
        columns = getPlanogramItemColumns();
        break;
      case ENTITY_TYPES.orders:
        columns = getOrderItemColumns();
        break;
      default:
        columns = [];
    }
    return columns;
  }, [entityType, updatedColumns, discountHeaders, isExportAll]);

  const { data: plannerNotesData } =
    notesApi.endpoints.getNotesForPlanogram.useQuery({
      noteType: NOTE_TYPE_VALUE.planner,
      storeId: store.id,
    });

  const { data: retailerNotesData } =
    notesApi.endpoints.getNotesForPlanogram.useQuery({
      noteType: NOTE_TYPE_VALUE.retailer,
      storeId: store.id,
    });

  const getTableContent = setTableContent({
    body,
    entityType,
    selectedIds,
    isExportAll,
    store,
    plannerNotesData,
    retailerNotesData,
  });

  const extractedExcelValues = useStableCallback(() => {
    const extractedHeaders = [
      "Store #",
      "Store Name",
      "Selected",
      ...map(headers, "headerName"),
    ];
    const fields = ["store", "storeName", "selected", ...map(headers, "field")];
    const extractedBody = getTableContent(fields);
    return {
      header: extractedHeaders,
      body: extractedBody,
    };
  });

  const currentFileName = useMemo(
    () =>
      isExportAll
        ? `OSO-${startCase(entityType)}-Global-Report${
            !!store?.memberNumber && `-${store?.memberNumber}`
          }-${new Date().toLocaleDateString()}`
        : `OSO-${startCase(entityType)}-Report${
            !!store?.memberNumber && `-${store?.memberNumber}`
          }-${new Date().toLocaleDateString()}`,
    [isExportAll, entityType, store?.memberNumber]
  );

  const handleDownloadExcel = useStableCallback(() => {
    const { header, body } = extractedExcelValues();
    downloadExcel({
      fileName: currentFileName,
      sheet: startCase(
        isExportAll ? `${entityType} global report` : `${entityType} report`
      ),
      tablePayload: {
        header,
        body,
      },
    });
  });

  return (
    <Tooltip title="Download Excel (.xls)">
      <Button
        id="export-btn"
        disableElevation
        size="small"
        onClick={handleDownloadExcel}
        endIcon={<FileDownloadOutlinedIcon />}
        sx={exportButtonSx}
      >
        Export
      </Button>
    </Tooltip>
  );
};

export default memo(ExportButton);
